import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const fetchPortfolio = createAsyncThunk('portfolio/fetch', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/portfolio');
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Failed to fetch portfolio');
  }
});

export const fetchPositions = createAsyncThunk('portfolio/fetchPositions', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/portfolio/positions');
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Failed to fetch positions');
  }
});

export const fetchSaxoStatus = createAsyncThunk('portfolio/fetchSaxoStatus', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/saxo/status');
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Failed to fetch Saxo status');
  }
});

export const fetchPerformance = createAsyncThunk('portfolio/fetchPerformance', async (range = '1m', { rejectWithValue }) => {
  try {
    const response = await api.get(`/portfolio/performance?range=${range}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Failed to fetch performance');
  }
});

export const fetchClosedPositions = createAsyncThunk('portfolio/fetchClosedPositions', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/portfolio/closed-positions');
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Failed to fetch closed positions');
  }
});


export const fetchWatchlist = createAsyncThunk('portfolio/fetchWatchlist', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/watchlist');
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Failed to fetch watchlist');
  }
});

export const addToWatchlist = createAsyncThunk('portfolio/addToWatchlist', async ({ symbol, description }, { rejectWithValue }) => {
  try {
    const response = await api.post('/watchlist', { symbol, description });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Failed to add to watchlist');
  }
});

export const removeFromWatchlist = createAsyncThunk('portfolio/removeFromWatchlist', async (symbol, { rejectWithValue }) => {
  try {
    await api.delete(`/watchlist/${encodeURIComponent(symbol)}`);
    return symbol;
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Failed to remove from watchlist');
  }
});

export const fetchAnalysisExclusions = createAsyncThunk('portfolio/fetchAnalysisExclusions', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/portfolio/analysis-exclusions');
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Failed to fetch analysis exclusions');
  }
});

export const setAnalysisExclusion = createAsyncThunk('portfolio/setAnalysisExclusion', async ({ symbol, excluded }, { rejectWithValue }) => {
  try {
    const response = await api.put('/portfolio/analysis-exclusions', { symbol, excluded });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || "Impossible de mettre à jour l'exclusion");
  }
});

export const fetchStockRumors = createAsyncThunk(
  'portfolio/fetchStockRumors',
  async ({ symbol, description, language = 'English', forceRefresh = false }, { getState, rejectWithValue }) => {
    const cacheKey = `${symbol}_${language}`;
    if (!forceRefresh) {
      const cached = getState().portfolio.stockRumors[cacheKey];
      if (cached) return { cacheKey, data: cached, cached: true };
    }
    try {
      const response = await api.post('/insights/rumors', { symbol, description, language, forceRefresh });
      return { cacheKey, data: response.data, cached: false };
    } catch (error) {
      return rejectWithValue({ cacheKey, error: error.response?.data?.error || 'Failed to fetch rumors' });
    }
  }
);

export const fetchBatchRumors = createAsyncThunk(
  'portfolio/fetchBatchRumors',
  async ({ stocks, language = 'English', forceRefresh = false }, { rejectWithValue }) => {
    try {
      const response = await api.post('/insights/rumors/batch', { stocks, language, forceRefresh });
      return { results: response.data.results, language };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch batch rumors');
    }
  }
);

export const fetchRumorsThread = createAsyncThunk(
  'portfolio/fetchRumorsThread',
  async ({ stocks, language = 'English', forceRefresh = false }, { rejectWithValue }) => {
    try {
      const response = await api.post('/insights/rumors/thread', { stocks, language, forceRefresh });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to generate thread');
    }
  }
);

export const fetchLatestNews = createAsyncThunk(
  'portfolio/fetchLatestNews',
  async ({ stocks, language = 'English', forceRefresh = false }, { rejectWithValue }) => {
    try {
      const response = await api.post('/insights/news', { stocks, language, forceRefresh });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch news');
    }
  }
);

export const fetchDividends = createAsyncThunk('portfolio/fetchDividends', async ({ tab = 'paid', year } = {}, { rejectWithValue }) => {
  try {
    let url = `/portfolio/dividends?tab=${tab}`;
    if (year && tab === 'paid') url += `&year=${year}`;
    const response = await api.get(url);
    return { tab, data: response.data };
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Failed to fetch dividends');
  }
});

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState: {
    summary: null,
    positions: [],
    performance: null,
    performanceLoading: false,
    dividends: { incoming: [], paid: [] },
    dividendsLoading: false,
    closedPositions: null,
    closedPositionsLoading: false,
    watchlist: [],
    watchlistLoading: false,
    watchlistError: null,
    analysisExclusions: [],
    analysisExclusionsError: null,
    exclusionPending: {},
    stockRumors: {},
    stockRumorsLoading: {},
    stockRumorsError: null,
    rumorsThread: null,
    rumorsThreadLoading: false,
    rumorsThreadError: null,
    latestNews: null,
    latestNewsLoading: false,
    latestNewsError: null,
    saxoConnected: false,
    saxoStatusLoading: false,
    saxoStatusChecked: false,
    saxoAuthUrl: null,
    backendAvailable: true,
    loading: false,
    error: null,
  },
  reducers: {
    clearPortfolioError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortfolio.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPortfolio.fulfilled, (state, action) => {
        state.loading = false;
        state.summary = action.payload;
        state.positions = action.payload.positions || [];
      })
      .addCase(fetchPortfolio.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPositions.fulfilled, (state, action) => {
        state.positions = action.payload;
      })
      .addCase(fetchSaxoStatus.pending, (state) => {
        state.saxoStatusLoading = true;
      })
      .addCase(fetchSaxoStatus.fulfilled, (state, action) => {
        state.saxoStatusLoading = false;
        state.saxoStatusChecked = true;
        state.backendAvailable = true;
        state.saxoConnected = action.payload.connected;
        state.saxoAuthUrl = action.payload.auth_url;
      })
      .addCase(fetchSaxoStatus.rejected, (state, action) => {
        state.saxoStatusLoading = false;
        state.saxoStatusChecked = true;
        // If no response at all, backend is unreachable
        if (!action.payload || action.payload === 'Failed to fetch Saxo status') {
          state.backendAvailable = false;
        }
      })
      .addCase(fetchPerformance.pending, (state) => {
        state.performanceLoading = true;
      })
      .addCase(fetchPerformance.fulfilled, (state, action) => {
        state.performanceLoading = false;
        state.performance = action.payload;
      })
      .addCase(fetchPerformance.rejected, (state) => {
        state.performanceLoading = false;
      })
      .addCase(fetchDividends.pending, (state) => {
        state.dividendsLoading = true;
      })
      .addCase(fetchDividends.fulfilled, (state, action) => {
        state.dividendsLoading = false;
        const { tab, data } = action.payload;
        if (tab === 'paid') {
          state.dividends.paid = data.transactions || [];
        } else {
          state.dividends.incoming = data.transactions || [];
        }
      })
      .addCase(fetchDividends.rejected, (state) => {
        state.dividendsLoading = false;
      })
      .addCase(fetchClosedPositions.pending, (state) => {
        state.closedPositionsLoading = true;
      })
      .addCase(fetchClosedPositions.fulfilled, (state, action) => {
        state.closedPositionsLoading = false;
        state.closedPositions = action.payload;
      })
      .addCase(fetchClosedPositions.rejected, (state) => {
        state.closedPositionsLoading = false;
      })
      .addCase(fetchWatchlist.pending, (state) => {
        state.watchlistLoading = true;
        state.watchlistError = null;
      })
      .addCase(fetchWatchlist.fulfilled, (state, action) => {
        state.watchlistLoading = false;
        state.watchlist = action.payload || [];
      })
      .addCase(fetchWatchlist.rejected, (state, action) => {
        state.watchlistLoading = false;
        state.watchlistError = action.payload || action.error.message || 'Failed to fetch watchlist';
      })
      .addCase(addToWatchlist.fulfilled, (state, action) => {
        const index = state.watchlist.findIndex(w => w.symbol === action.payload.symbol);
        if (index < 0) state.watchlist.unshift(action.payload);
        else state.watchlist[index] = action.payload;
      })
      .addCase(removeFromWatchlist.fulfilled, (state, action) => {
        state.watchlist = state.watchlist.filter(w => w.symbol !== action.payload);
      })
      .addCase(fetchAnalysisExclusions.fulfilled, (state, action) => {
        state.analysisExclusions = action.payload;
      })
      .addCase(setAnalysisExclusion.pending, (state, action) => {
        state.exclusionPending[action.meta.arg.symbol] = true;
        state.analysisExclusionsError = null;
      })
      .addCase(setAnalysisExclusion.fulfilled, (state, action) => {
        delete state.exclusionPending[action.meta.arg.symbol];
        state.analysisExclusions = action.payload;
      })
      .addCase(setAnalysisExclusion.rejected, (state, action) => {
        delete state.exclusionPending[action.meta.arg.symbol];
        state.analysisExclusionsError = action.payload || action.error.message;
      })
      .addCase(fetchStockRumors.pending, (state, action) => {
        const sym = action.meta.arg.symbol;
        state.stockRumorsLoading[sym] = true;
        state.stockRumorsError = null;
        if (state.stockRumorsErrors) delete state.stockRumorsErrors[sym];
      })
      .addCase(fetchStockRumors.fulfilled, (state, action) => {
        const sym = action.meta.arg.symbol;
        delete state.stockRumorsLoading[sym];
        if (!action.payload.cached) {
          state.stockRumors[action.payload.cacheKey] = action.payload.data;
        }
      })
      .addCase(fetchStockRumors.rejected, (state, action) => {
        const sym = action.meta.arg.symbol;
        delete state.stockRumorsLoading[sym];
        state.stockRumorsError = action.payload?.error || 'Failed to fetch rumors';
        if (!state.stockRumorsErrors) state.stockRumorsErrors = {};
        state.stockRumorsErrors[sym] = action.payload?.error || 'Failed to fetch rumors';
      })
      .addCase(fetchBatchRumors.pending, (state, action) => {
        const stocks = action.meta.arg.stocks || [];
        stocks.forEach(s => { state.stockRumorsLoading[s.symbol] = true; });
        state.stockRumorsError = null;
        state.stockRumorsErrors = {};
      })
      .addCase(fetchBatchRumors.fulfilled, (state, action) => {
        const { results, language } = action.payload;
        results.forEach(r => {
          delete state.stockRumorsLoading[r.symbol];
          if (r.rumors) {
            const cacheKey = `${r.symbol}_${language}`;
            state.stockRumors[cacheKey] = r.rumors;
          }
          if (r.error) {
            if (!state.stockRumorsErrors) state.stockRumorsErrors = {};
            state.stockRumorsErrors[r.symbol] = r.error;
          }
        });
      })
      .addCase(fetchBatchRumors.rejected, (state, action) => {
        state.stockRumorsLoading = {};
        state.stockRumorsError = action.payload || 'Failed to fetch batch rumors';
      })
      .addCase(fetchRumorsThread.pending, (state) => {
        state.rumorsThreadLoading = true;
        state.rumorsThreadError = null;
      })
      .addCase(fetchRumorsThread.fulfilled, (state, action) => {
        state.rumorsThreadLoading = false;
        state.rumorsThread = action.payload;
      })
      .addCase(fetchRumorsThread.rejected, (state, action) => {
        state.rumorsThreadLoading = false;
        state.rumorsThreadError = action.payload || 'Failed to generate thread';
      })
      .addCase(fetchLatestNews.pending, (state) => {
        state.latestNewsLoading = true;
        state.latestNewsError = null;
      })
      .addCase(fetchLatestNews.fulfilled, (state, action) => {
        state.latestNewsLoading = false;
        state.latestNews = action.payload;
      })
      .addCase(fetchLatestNews.rejected, (state, action) => {
        state.latestNewsLoading = false;
        state.latestNewsError = action.payload || 'Failed to fetch news';
      });
  },
});

export const { clearPortfolioError } = portfolioSlice.actions;
export default portfolioSlice.reducer;
